import { ActionReducerMapBuilder, createSlice } from '@reduxjs/toolkit';
import { Championship } from '../../domain/interfaces/Championship';
import { Stage } from '../../domain/interfaces/Stage';
import {
  clearSelectedChampionship,
  createChampionshipGame,
  deleteChampionshipGame,
  fetchChampionship,
  selectChampionshipStage,
  showChampionshipStageForms,
  updateApplicationStatus,
  upsertChampionshipGame,
  upsertChampionship,
  upsertChampionshipSettings,
  fetchChampionshipTeams, fetchChampionshipGames, upsertSymbolicTeam,
  selectSymbolicTeam, deleteChampionshipStage,
} from './actions';
import { Round } from '../../domain/interfaces/Round';
import { SymbolicTeam } from '../../domain/interfaces/SymbolicTeam';
import { TeamGroupStatistics } from '../../domain/interfaces/TeamGroupStatistics';

type StageGroupsStatistics = TeamGroupStatistics | null;

export type ChampionshipStateData = Championship & {
  isStageSaving: boolean;
  showStageForms: boolean;
  isMatchSaving: boolean;
  isSymbolicTeamSaving: boolean;
  selectedStage: Stage | null;
  stageGroupsStatistics: StageGroupsStatistics;
  selectedSymbolicTeam: SymbolicTeam | null;
  teamsFlags: {
    isLoading: boolean;
    isLoaded: boolean;
  };
  stagesFlags: {
    isLoading: boolean;
    isLoaded: boolean;
  };
  gamesFlags: {
    isLoading: boolean;
    isLoaded: boolean;
  };
}

export type TourneyChampionshipSliceState = {
  isLoading: boolean;
  isLoaded: boolean;
  isSaving: boolean;
  updatingApplicationTeamId: number | null;
  data: ChampionshipStateData | null;
};

const initialState: TourneyChampionshipSliceState = {
  isLoading: false,
  isLoaded: false,
  isSaving: false,
  updatingApplicationTeamId: null,
  data: null,
};

const defaultFlagsState = {
  isLoading: false,
  isLoaded: false,
};

const defaultChampionshipData = {
  isStageSaving: false,
  showStageForms: false,
  isMatchSaving: false,
  isSymbolicTeamSaving: false,
  selectedStage: null,
  stageGroupsStatistics: null,
  selectedSymbolicTeam: null,
  teamsFlags: {...defaultFlagsState},
  stagesFlags: {...defaultFlagsState},
  gamesFlags: {...defaultFlagsState},
};

function createFetchChampionshipReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(fetchChampionship.pending, (state) => {
    state.isLoading = true;
  });

  builder.addCase(fetchChampionship.fulfilled, (state, { payload }) => {
    state.isLoading = false;
    state.isLoaded = true;
    state.data = {
      ...defaultChampionshipData,
      ...payload,
    } as ChampionshipStateData;
  });

  builder.addCase(fetchChampionship.rejected, (state) => {
    state.isLoading = false;
    state.isLoaded = false;
  });
}

function createFetchChampionshipTeamsReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(fetchChampionshipTeams.pending, (state) => {
    if (!state.data) {
      return;
    }

    state.data.teamsFlags.isLoading = true;
  });

  builder.addCase(fetchChampionshipTeams.fulfilled, (state, { payload }) => {
    if (!state.data) {
      return;
    }

    state.data = {
      ...state.data,
      teamsFlags: {
        isLoading: false,
        isLoaded: true,
      },
      applications: payload.applications,
      teams: payload.teams,
    };
  });

  builder.addCase(fetchChampionshipTeams.rejected, (state) => {
    if (!state.data) {
      return;
    }

    state.data = {
      ...state.data,
      teamsFlags: {
        isLoading: false,
        isLoaded: true,
      },
    };
  });
}

function createFetchChampionshipGamesReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(fetchChampionshipGames.pending, (state) => {
    if (!state.data) {
      return;
    }

    state.data.gamesFlags.isLoading = true;
  });

  builder.addCase(fetchChampionshipGames.fulfilled, (state, { payload }) => {
    if (!state.data) {
      return;
    }

    state.data = {
      ...state.data,
      gamesFlags: {
        isLoading: false,
        isLoaded: true,
      },
      games: payload.games,
    };
  });

  builder.addCase(fetchChampionshipGames.rejected, (state) => {
    if (!state.data) {
      return;
    }

    state.data = {
      ...state.data,
      gamesFlags: {
        isLoading: false,
        isLoaded: true,
      },
    };
  });
}

function createDeleteChampionshipStageReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(deleteChampionshipStage.pending, (state) => {
    if (!state.data) {
      return;
    }

    state.data.stagesFlags.isLoading = true;
  });

  builder.addCase(deleteChampionshipStage.rejected, (state) => {
    if (!state.data) {
      return;
    }

    state.data.stagesFlags.isLoading = false;
  });

  builder.addCase(deleteChampionshipStage.fulfilled, (state, { payload, meta }) => {
    const stageId = meta.arg;

    if (!state.data) {
      return;
    }

    const stages = payload
      ? state.data.stages.filter(((stage) => stage.id !== stageId))
      : state.data.stages;

    state.data = {
      ...state.data,
      stages,
      stagesFlags: {
        ...state.data.stagesFlags,
        isLoading: false,
      },
    };
  });
}

function createCreateChampionshipGameReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(createChampionshipGame.fulfilled, (state, { payload }) => {
    if (!state.data || !payload) {
      return;
    }

    const games = [
      ...state.data.games,
      payload,
    ].sort((a, b) => b.date.valueOf() - a.date.valueOf());

    state.data = {
      ...state.data,
      games,
    };
  });
}

function createUpdateChampionshipGameReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(upsertChampionshipGame.pending, (state) => {
    if (!state.data) {
      return;
    }

    state.data = {
      ...state.data,
      isMatchSaving: true,
    };
  });

  builder.addCase(upsertChampionshipGame.rejected, (state) => {
    if (!state.data) {
      return;
    }

    state.data = {
      ...state.data,
      isMatchSaving: false,
    };
  });

  builder.addCase(upsertChampionshipGame.fulfilled, (state, { payload }) => {
    if (!state.data || !payload) {
      return;
    }

    let updatedGames = state.data.games;
    let updatedStages = state.data.stages;

    const hasGameInSages = updatedStages.some(({ matches }) => matches.some((game) => game.id === payload.id));
    const hasGameInAllGames = updatedGames.some((game) => game.id === payload.id);

    if (!payload.round) {
      if (hasGameInSages) {
        updatedStages = updatedStages.map((stage) => {
          if (stage.matches.some((match) => match.id === payload.id)) {
            return {
              ...stage,
              matches: stage.matches.filter((match) => match.id !== payload.id),
              rounds: stage.rounds.map((round) => {
                if (round.matchIds.includes(payload.id)) {
                  return {
                    ...round,
                    matchIds: round.matchIds.filter((id) => id !== payload.id),
                  }
                }

                return round;
              }),
            };
          }

          return stage;
        });
      }

      if (hasGameInAllGames) {
        updatedGames = updatedGames.map((game) => {
          if (game.id === payload.id) {
            return payload;
          }

          return game;
        });
      } else {
        updatedGames = [
          ...updatedGames,
          payload,
        ].sort((a, b) => b.date.valueOf() - a.date.valueOf());
      }
    } else {
      if (hasGameInAllGames) {
        updatedGames = updatedGames.filter((game) => game.id !== payload.id);
      }

      if (hasGameInSages) {
        updatedStages = updatedStages.map((stage) => {
          const updatedRounds = stage.rounds
            .map((round) => {
              const hasMatch = round.matchIds.includes(payload.id);

              if (hasMatch && payload.round!.id !== round.id) {
                return null;
              }

              if (!hasMatch && payload.round!.id === round.id) {
                return {
                  ...round,
                  matchIds: [
                    ...round.matchIds,
                    payload.id,
                  ],
                };
              }

              return round
            })
            .filter(Boolean) as Round[];

          const updatedStageMatches = stage.matches.map((match) => {
            if (match.id === payload.id) {
              return payload;
            }

            return match;
          });

          return {
            ...stage,
            rounds: updatedRounds,
            matches: updatedStageMatches,
          };
        });
      } else {
        updatedStages = updatedStages.map((stage) => {
          if (stage.id !== payload.round!.stageId) {
            return stage;
          }

          return {
            ...stage,
            rounds: stage.rounds.map((round) => {
              if (round.id !== payload.round!.id) {
                return round;
              }

              return {
                ...round,
                matchIds: [
                  ...round.matchIds,
                  payload.id,
                ],
              };
            }),
            matches: [
              ...stage.matches,
              payload,
            ].sort((a, b) => b.date.valueOf() - a.date.valueOf()),
          };
        });
      }
    }

    const selectedStage = state.data.selectedStage;
    const updatedSelectedState = selectedStage
      ? updatedStages.find((stage) => stage.id === selectedStage.id) || null
      : selectedStage;

    state.data = {
      ...state.data,
      isMatchSaving: false,
      games: updatedGames,
      stages: updatedStages,
      selectedStage: updatedSelectedState
    };
  });
}

function createDeleteChampionshipGameReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(deleteChampionshipGame.fulfilled, (state, { payload }) => {
    if (!state.data || !payload) {
      return;
    }

    const games = state.data.games.filter((game) => game.id !== payload);
    const stages = state.data.stages.map((stage) => {
      const existMatch = stage.matches.find((match) => match.id === payload);
      if (!existMatch) {
        return stage;
      }

      const filteredMatches = stage.matches.filter((match) => match.id !== payload);
      const filteredRounds = stage.rounds.map((round) => {
        if (round.matchIds.includes(payload)) {
          return {
            ...round,
            matchIds: round.matchIds.filter((matchId) => matchId !== payload),
          };
        }

        return round;
      });

      return {
        ...stage,
        matches: filteredMatches,
        rounds: filteredRounds,
      };
    });

    let selectedStage = state.data.selectedStage;
    if (selectedStage) {
      selectedStage = stages.find((stage) => stage.id === selectedStage?.id) || null;
    }

    state.data = {
      ...state.data,
      games,
      stages,
      selectedStage,
    };
  });
}

function createUpsertChampionshipReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(upsertChampionship.pending, (state) => {
    state.isSaving = true;
  });

  builder.addCase(upsertChampionship.fulfilled, (state) => {
    state.isSaving = false;
  });

  builder.addCase(upsertChampionship.rejected, (state) => {
    state.isSaving = false;
  });
}

function createUpsertChampionshipSettingsReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(upsertChampionshipSettings.pending, (state) => {
    state.isSaving = true;
  });

  builder.addCase(upsertChampionshipSettings.fulfilled, (state) => {
    state.isSaving = false;
  });

  builder.addCase(upsertChampionshipSettings.rejected, (state) => {
    state.isSaving = false;
  });
}

function createUpsertChampionshipApplicationReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(updateApplicationStatus.pending, (state, { meta }) => {
    state.updatingApplicationTeamId = meta.arg.teamId;
  });

  builder.addCase(updateApplicationStatus.fulfilled, (state, { payload }) => {
    if (!state.data) {
      return;
    }

    const applications = state.data.applications || [];

    state.updatingApplicationTeamId = null;
    state.data = {
      ...state.data,
      applications: applications.map((app) => {
        if (app.teamId === payload.teamId && app.championshipId === payload.championshipId) {
          return {
            ...app,
            status: payload.status
          };
        }

        return app;
      }),
    };
  });

  builder.addCase(updateApplicationStatus.rejected, (state) => {
    state.updatingApplicationTeamId = null;
  });
}

function createUpsertSymbolicTeamReducer(builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) {
  builder.addCase(upsertSymbolicTeam.pending, (state) => {
    if (!state.data) {
      return;
    }

    state.data.isSymbolicTeamSaving = true;
  });

  builder.addCase(upsertSymbolicTeam.fulfilled, (state, { meta, payload }) => {
    if (!state.data) {
      return;
    }

    if (meta.arg.id) {
      state.data.symbolicTeams = state.data.symbolicTeams.map((item) => {
        if (item.id === payload.id) {
          return payload;
        }

        return item;
      });
    } else {
      state.data.symbolicTeams = [
        ...state.data.symbolicTeams,
        payload,
      ];
    }

    state.data.selectedSymbolicTeam = payload;
    state.data.isSymbolicTeamSaving = false;
  });

  builder.addCase(upsertSymbolicTeam.rejected, (state) => {
    if (!state.data) {
      return;
    }

    state.data.isSymbolicTeamSaving = false;
  });
}

const slice = createSlice({
  name: 'tourneys/championship',
  initialState,
  reducers: {},
  extraReducers: (builder: ActionReducerMapBuilder<TourneyChampionshipSliceState>) => {
    createFetchChampionshipReducer(builder);
    createFetchChampionshipTeamsReducer(builder);
    createFetchChampionshipGamesReducer(builder);
    createDeleteChampionshipStageReducer(builder);

    createCreateChampionshipGameReducer(builder);
    createUpdateChampionshipGameReducer(builder);
    createDeleteChampionshipGameReducer(builder);
    createUpsertChampionshipReducer(builder);
    createUpsertChampionshipSettingsReducer(builder);
    createUpsertChampionshipApplicationReducer(builder);

    createUpsertSymbolicTeamReducer(builder);

    builder.addCase(clearSelectedChampionship, (state) => {
      state.data = null;
    });

    builder.addCase(selectChampionshipStage, (state, { payload }) => {
      if (!state.data) {
        return;
      }

      state.data.selectedStage = payload;
    });

    builder.addCase(showChampionshipStageForms, (state, { payload }) => {
      if (!state.data) {
        return;
      }

      state.data.showStageForms = payload;
    });

    builder.addCase(selectSymbolicTeam, (state, { payload }) => {
      if (!state.data) {
        return;
      }

      state.data.selectedSymbolicTeam = payload;
    });
  },
});

export default slice.reducer;
